import * as React from "react";
import { Link } from "@tanstack/react-router";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";

interface HeroProps {
  headline: string;
  subheadline: string;
  primaryCTA: { text: string };
  onPrimaryCTAClick?: () => void;
  trustIndicator?: string;
}

/**
 * Hero (Neo-Brutalist Version)
 *
 * Hero section with:
 * - Bold, solid typography
 * - Neo-brutalist badges and buttons
 * - Staggered entrance animations
 * - Decorative geometric elements
 */
export function Hero({
  headline,
  subheadline,
  primaryCTA,
  onPrimaryCTAClick,
  trustIndicator,
}: HeroProps) {
  const heroRef = React.useRef<HTMLDivElement>(null);
  const [scrollProgress, setScrollProgress] = React.useState(0);

  React.useEffect(() => {
    const handleScroll = () => {
      if (!heroRef.current) return;
      const rect = heroRef.current.getBoundingClientRect();
      const progress = Math.min(1, Math.max(0, -rect.top / rect.height));
      setScrollProgress(progress);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Subtle parallax (less dramatic than before)
  const translateY = scrollProgress * 50;

  return (
    <section
      ref={heroRef}
      className="relative min-h-[100vh] flex items-center justify-center pt-24 lg:pt-32 pb-16 lg:pb-24 overflow-hidden bg-[#FFFBF0]"
    >
      {/* Decorative shapes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Top-left shape */}
        <div
          className={cn(
            "absolute -top-8 -left-8 w-32 h-32",
            "bg-[#FFDC00] rounded-3xl",
            "border-3 border-black",
            "rotate-12 opacity-30"
          )}
        />
        {/* Top-right shape */}
        <div
          className={cn(
            "absolute top-20 -right-16 w-48 h-48",
            "bg-[#FF73A9] rounded-full",
            "border-3 border-black",
            "opacity-20"
          )}
        />
        {/* Bottom-left shape */}
        <div
          className={cn(
            "absolute bottom-32 -left-12 w-24 h-24",
            "bg-[#3F5EF4] rounded-2xl",
            "border-3 border-black",
            "-rotate-6 opacity-25"
          )}
        />
        {/* Bottom-right shape */}
        <div
          className={cn(
            "absolute -bottom-8 right-1/4 w-40 h-40",
            "bg-[#00EB90] rounded-3xl",
            "border-3 border-black",
            "rotate-12 opacity-20"
          )}
        />
      </div>

      <div
        className="container mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10"
        style={{
          transform: `translateY(${translateY}px)`,
          transition: "transform 0.1s linear",
        }}
      >
        {/* Trust indicator badge */}
        {trustIndicator && (
          <div className="animate-fade-up stagger-1 opacity-0 mb-8">
            <span
              className={cn(
                "inline-flex items-center gap-2 px-4 py-2 rounded-full",
                "bg-[#00EB90]",
                "border-3 border-black",
                "shadow-[3px_3px_0_0_#000000]",
                "text-sm font-semibold text-black font-['Sora']"
              )}
            >
              <span className="w-2 h-2 rounded-full bg-black" />
              {trustIndicator}
            </span>
          </div>
        )}

        {/* Headline */}
        <h1
          className={cn(
            "animate-fade-up stagger-2 opacity-0",
            "text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl",
            "font-black tracking-tight leading-[1.1]",
            "font-['Epilogue']",
            "max-w-5xl mx-auto mb-6 lg:mb-8",
            "text-[#1A1A1A]"
          )}
        >
          {headline}
        </h1>

        {/* Subheadline */}
        <p
          className={cn(
            "animate-fade-up stagger-3 opacity-0",
            "text-base sm:text-lg lg:text-xl",
            "text-[#666666] leading-relaxed",
            "font-['Sora']",
            "max-w-2xl mx-auto mb-10 lg:mb-12"
          )}
        >
          {subheadline}
        </p>

        {/* CTAs */}
        <div
          className={cn(
            "animate-fade-up stagger-4 opacity-0",
            "flex flex-col sm:flex-row items-center justify-center gap-4"
          )}
        >
          {onPrimaryCTAClick ? (
            <Button size="lg" onClick={onPrimaryCTAClick}>
              {primaryCTA.text}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Link to="/signup">
              <Button size="lg">
                {primaryCTA.text}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          )}
        </div>

        {/* Decorative divider */}
        <div
          className={cn(
            "animate-fade-up stagger-5 opacity-0",
            "mt-16 lg:mt-24"
          )}
        >
          <div className="h-1 bg-black mx-auto w-32 rounded-full" />
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        className={cn(
          "absolute bottom-8 left-1/2 -translate-x-1/2",
          "animate-bounce"
        )}
        style={{ opacity: Math.max(0, 0.7 - scrollProgress * 2) }}
      >
        <div
          className={cn(
            "w-10 h-14 rounded-full",
            "border-3 border-black",
            "bg-white",
            "shadow-[3px_3px_0_0_#000000]",
            "flex items-start justify-center p-3"
          )}
        >
          <div className="w-1.5 h-3 rounded-full bg-black animate-pulse" />
        </div>
      </div>
    </section>
  );
}
